"use client";

import React, { useState, useMemo } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  Copy,
  Check,
  RotateCcw,
  Sliders,
  Cpu,
  Server,
  FileText,
  Terminal,
  Activity,
  Zap,
  Lock,
  Sparkles,
} from "lucide-react";

type Strategy = "naive_dump" | "filtered_mcp" | "lxc_worker";
type TaskType = "battery_check" | "zombie_hunt" | "log_audit" | "actuator_call" | "heating_audit";

interface Preset {
  id: string;
  name: string;
  description: string;
  fleetSize: number;
  strategy: Strategy;
  task: TaskType;
  humanApproval: boolean;
}

const PRESETS: Preset[] = [
  {
    id: "battery_preset",
    name: "1. Batterie-Check (Wohnung)",
    description: "Sucht nur Sensoren unter 20% – spart 99% Datenmüll.",
    fleetSize: 65,
    strategy: "filtered_mcp",
    task: "battery_check",
    humanApproval: false,
  },
  {
    id: "zombie_preset",
    name: "2. Zombie-Finder (Haus)",
    description: "Findet stumme Geräte, die seit 24 Stunden offline sind.",
    fleetSize: 280,
    strategy: "filtered_mcp",
    task: "zombie_hunt",
    humanApproval: false,
  },
  {
    id: "heating_preset",
    name: "3. Heiz-Detektiv (Langzeit-Tuning)",
    description: "Misst Auskühlraten über Tage und findet Einsparpotenziale.",
    fleetSize: 280,
    strategy: "filtered_mcp",
    task: "heating_audit",
    humanApproval: false,
  },
  {
    id: "naive_preset",
    name: "4. Der geschwätzige Scan (Anti-Pattern)",
    description: "Liest das ganze Haus auf einmal aus – sprengt den Speicher!",
    fleetSize: 650,
    strategy: "naive_dump",
    task: "battery_check",
    humanApproval: false,
  },
  {
    id: "actuator_preset",
    name: "5. Heizungs-Schutz (Sicherheits-Gate)",
    description: "Schalten erst nach deiner Bestätigung auf dem Handy.",
    fleetSize: 280,
    strategy: "filtered_mcp",
    task: "actuator_call",
    humanApproval: true,
  },
];

export default function HomeAssistantMcpAuditorPlayground() {
  const [fleetSize, setFleetSize] = useState<number>(280);
  const [strategy, setStrategy] = useState<Strategy>("filtered_mcp");
  const [task, setTask] = useState<TaskType>("battery_check");
  const [humanApproval, setHumanApproval] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<"payload" | "markdown" | "alternatives">("payload");
  const [copied, setCopied] = useState<boolean>(false);

  // Apply Preset
  const handleApplyPreset = (p: Preset) => {
    setFleetSize(p.fleetSize);
    setStrategy(p.strategy);
    setTask(p.task);
    setHumanApproval(p.humanApproval);
  };

  // Metrics calculation
  const metrics = useMemo(() => {
    const isActuator = task === "actuator_call";
    let tokenInput = 0;
    let relevantEntities = 0;
    let transferBytes = 0;

    if (task === "battery_check") {
      relevantEntities = Math.max(1, Math.round(fleetSize * 0.18)); // ~18% battery devices
    } else if (task === "zombie_hunt") {
      relevantEntities = Math.max(1, Math.round(fleetSize * 0.05)); // ~5% offline nodes
    } else if (task === "heating_audit") {
      relevantEntities = Math.max(2, Math.round(fleetSize * 0.08)); // ~8% climate/thermostats
    } else if (task === "log_audit") {
      relevantEntities = 12; // 12 critical log events
    } else {
      relevantEntities = 1; // single actuator
    }

    if (strategy === "naive_dump") {
      tokenInput = fleetSize * 78 + 350;
      transferBytes = fleetSize * 512;
    } else if (strategy === "filtered_mcp") {
      tokenInput = relevantEntities * 68 + 240;
      transferBytes = relevantEntities * 420;
    } else {
      tokenInput = relevantEntities * 22 + 90;
      transferBytes = relevantEntities * 110;
    }

    const lanLatencyMs = 0.8;
    const tokenInferenceEstMs = Math.round(tokenInput * 0.12);
    const totalLatencyMs = lanLatencyMs + tokenInferenceEstMs;

    let safetyStatus: "PASS" | "WARNUNG" | "BLOCKED" = "PASS";
    let safetyMessage = "Sicherer Lesezugriff: Der Agent guckt nur und fasst nichts an.";

    if (tokenInput > 30000) {
      safetyStatus = "WARNUNG";
      safetyMessage = "Achtung: Riesige Datenflut (>30.000 Wörter)! Hohe Kosten und langsame Antwort.";
    }

    if (isActuator) {
      if (!humanApproval) {
        safetyStatus = "BLOCKED";
        safetyMessage = "Stopp: Schalten blockiert! Erst deine Freigabe auf dem Smartphone schaltet den Thermostat.";
      } else {
        safetyStatus = "PASS";
        safetyMessage = "Freigabe erteilt: Du hast die Temperaturänderung auf 21.5°C bestätigt.";
      }
    }

    return {
      tokenInput,
      relevantEntities,
      transferBytes: Math.round(transferBytes / 1024),
      lanLatencyMs,
      totalLatencyMs,
      safetyStatus,
      safetyMessage,
      isActuator,
    };
  }, [fleetSize, strategy, task, humanApproval]);

  // Wire JSON-RPC Payload
  const jsonRpcPayload = useMemo(() => {
    if (task === "battery_check") {
      if (strategy === "naive_dump") {
        return JSON.stringify(
          {
            jsonrpc: "2.0",
            id: "ha_dump_naive",
            method: "ha_get_overview",
            params: {
              info: "Achtung: Liest unkomprimiert alle Entitäten ein",
              total_entities: fleetSize,
              include_all_attributes: true,
            },
          },
          null,
          2
        );
      }
      return JSON.stringify(
        {
          jsonrpc: "2.0",
          id: "ha_search_battery",
          method: "ha_search_entities",
          params: {
            domain: "sensor",
            device_class: "battery",
            state_filter: { operator: "<", value: 20 },
            fields: ["entity_id", "state", "attributes.friendly_name"],
          },
        },
        null,
        2
      );
    }

    if (task === "zombie_hunt") {
      return JSON.stringify(
        {
          jsonrpc: "2.0",
          id: "ha_search_zombies",
          method: "ha_search_entities",
          params: {
            states: ["unavailable", "unknown"],
            min_duration_hours: 24,
            fields: ["entity_id", "state", "last_changed"],
          },
        },
        null,
        2
      );
    }

    if (task === "log_audit") {
      return JSON.stringify(
        {
          jsonrpc: "2.0",
          id: "ha_check_logbook",
          method: "ha_get_logbook",
          params: {
            period_hours: 12,
            filter_levels: ["ERROR", "WARNING"],
            limit: 25,
          },
        },
        null,
        2
      );
    }

    if (task === "heating_audit") {
      return JSON.stringify(
        {
          jsonrpc: "2.0",
          id: "ha_heating_tuning",
          method: "ha_search_entities",
          params: {
            domain: "climate",
            include_history_days: 7,
            metrics: ["current_temperature", "target_temperature", "valve_position", "weather.outdoor_temp"],
            analysis: "thermal_inertia_and_cooling_rate",
          },
        },
        null,
        2
      );
    }

    return JSON.stringify(
      {
        jsonrpc: "2.0",
        id: "ha_climate_action",
        method: "ha_call_service",
        params: {
          domain: "climate",
          service: "set_temperature",
          target: { entity_id: "climate.wohnzimmer_thermostat" },
          service_data: { temperature: 21.5 },
          safety_guardrails: {
            human_in_the_loop_confirmed: humanApproval,
            max_temperature: 24.0,
          },
        },
      },
      null,
      2
    );
  }, [task, strategy, fleetSize, humanApproval]);

  // Generated SilverBullet Markdown Output
  const markdownOutput = useMemo(() => {
    const timestamp = "2026-10-23 08:00";
    if (task === "battery_check") {
      return `# 🔋 Morgen-Check: Batteriestatus
Erstellt am: \`${timestamp}\` durch Antigravity Agent
Gespeicherter Ort: \`/space/HomeAssistant/Batterie_Status.md\`

## ⚠️ Batterien unter 20% (Bitte bald tauschen)

| Raum | Gerät | Batteriestand | Empfohlene Batterie |
| :--- | :--- | :--- | :--- |
| **Flur** | Bewegungsmelder Eingang | **14 %** | 1x CR2032 Knopfzelle |
| **Badezimmer** | Klimasensor Dusche | **18 %** | 2x AAA Micro |
| **Büro** | Fensterkontakt Schreibtisch | **19 %** | 1x CR1632 Knopfzelle |

> [!TIP] Hausmeister-Tipp
> Die Batterie im Flur hält noch ca. 3–5 Tage. Ersatz liegt in der Schublade im Flur.`;
    }

    if (task === "zombie_hunt") {
      return `# 🧟 Zombie-Geräte (Seit über 24h stumm)
Erstellt am: \`${timestamp}\`
Gespeicherter Ort: \`/space/HomeAssistant/Zombies.md\`

## Nicht erreichbare Geräte (2 Stück)

* **Garten-Beleuchtung (Relais)**
  * Status: \`unavailable\` seit 38 Stunden
  * Ursache: Eventuell wurde der Hauptschalter draußen ausgeschaltet.
* **Balkon-Temperatursensor**
  * Status: \`unknown\` seit 29 Stunden
  * Ursache: Schwaches WLAN-Signal (-86 dBm).`;
    }

    if (task === "heating_audit") {
      return `# 🌡️ Heizungs-Tuning & Auskühl-Analyse (Woche 43)
Erstellt am: \`${timestamp}\` durch Antigravity Agent
Gespeicherter Ort: \`/space/HomeAssistant/Heizung_Tuning.md\`

## 🔬 Erkenntnisse aus 7 Tagen Temperatur-Messung

* **Wohnzimmer (Südseite):**
  * Auskühlrate nachts: Nur **0,28°C pro Stunde** (sehr gut gedämmt).
  * Testergebnis: Die Nachtabsenkung kann sicher von 18,5°C auf **17,0°C** abgesenkt werden.
  * Sonneneffekt: Ab 10:30 Uhr heizt die Sonne den Raum um +1,8°C auf. Das Vorheizen morgens kann um 45 Minuten verkürzt werden.
* **Büro (Nordseite):**
  * Auskühlrate nachts: **0,65°C pro Stunde** (kühlt spürbar schneller ab).
  * Auffälligkeit: Das Ventil stand zwischen 14:00 und 16:00 Uhr auf 100%, der Raum stieg aber nicht über 19°C.
  * Diagnose: *Möglicherweise klemmt der Ventilstift am Heizkörper oder der hydraulische Abgleich ist unzureichend.*

## 💡 Berechnetes Spar-Potenzial
* Durch 45 min kürzeres Morgenheizen und tiefere Nachtabsenkung: **ca. 8–11% Gas-/Heizenergie-Einsparung**.
* Nächster Testschritt: Nachtabsenkung 17,0°C über die nächste Woche validieren.`;
    }

    if (task === "log_audit") {
      return `# 📋 System-Logbuch Kurzbericht
Erstellt am: \`${timestamp}\`
Geprüftes Zeitfenster: Letzte 12 Stunden

* **Automatisierungs-Schleifen:** Keine Endlosschleifen gefunden (alles sauber).
* **Netzwerk:** 1x kurzes Timeout beim Zigbee-Stick um 04:12 Uhr (hat sich von selbst erholt).
* **Zustand:** System läuft stabil.`;
    }

    return `# 🔒 Aktor-Schaltprotokoll
Aktion: \`climate.set_temperature\` auf 21.5°C
Thermostat: Wohnzimmer
Freigabe durch Nutzer: ${humanApproval ? "✅ ERTEILT (Schaltvorgang ausgeführt)" : "❌ ABGELEHNT (Sicherheits-Stop)"}
Eingetragen in: \`/space/HomeAssistant/Schaltprotokoll.md\``;
  }, [task, humanApproval]);

  const handleCopy = (text: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative flex flex-col gap-6 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm dark:border-neutral-800 dark:bg-neutral-950 sm:p-6 min-h-[580px]">
      {/* Top Header */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 pb-4 dark:border-neutral-800">
          <div>
            <h3 className="font-heading text-lg font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <span>Der interaktive Hausmeister-Simulator</span>
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              Probiere aus, wie der Agent sparsam arbeitet, Notizen anlegt und sich von anderen Setups unterscheidet.
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleApplyPreset(PRESETS[0])}
            className="flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-50 dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-900 min-h-[44px]"
            title="Zurück zum Standard-Beispiel"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>Standard</span>
          </button>
        </div>

        {/* Preset Buttons */}
        <div className="mt-3 flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handleApplyPreset(p)}
              className="rounded-lg border border-purple-500/20 bg-purple-50/50 px-3 py-2 text-left text-xs font-medium text-purple-900 transition-colors hover:bg-purple-100/70 dark:bg-purple-950/20 dark:text-purple-300 dark:hover:bg-purple-900/40 min-h-[44px] flex flex-col justify-center"
            >
              <span className="font-bold">{p.name}</span>
              <span className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5">
                {p.description}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Fleet Size Slider */}
        <div className="flex flex-col justify-between rounded-xl border border-neutral-200/80 bg-neutral-50/50 p-4 dark:border-neutral-800/80 dark:bg-neutral-900/50">
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor="fleet-slider"
              className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 flex items-center gap-1.5"
            >
              <Cpu className="h-3.5 w-3.5 text-purple-500" />
              <span>Geräte im Smart Home:</span>
            </label>
            <span className="font-mono text-xs font-bold text-purple-600 dark:text-purple-400">
              {fleetSize} Geräte
            </span>
          </div>
          <input
            id="fleet-slider"
            type="range"
            min={20}
            max={1200}
            step={20}
            value={fleetSize}
            onChange={(e) => setFleetSize(Number(e.target.value))}
            className="w-full accent-purple-600 cursor-pointer min-h-[44px]"
            aria-label="Anzahl der Smart-Home-Geräte"
          />
          <div className="flex justify-between text-[10px] text-neutral-400 font-mono mt-1">
            <span>20 (Kleine Wohnung)</span>
            <span>280 (Haus)</span>
            <span>1200 (Großes Labor)</span>
          </div>
        </div>

        {/* Task Selection */}
        <div className="flex flex-col justify-between rounded-xl border border-neutral-200/80 bg-neutral-50/50 p-4 dark:border-neutral-800/80 dark:bg-neutral-900/50">
          <label
            htmlFor="task-select"
            className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 flex items-center gap-1.5 mb-2"
          >
            <Activity className="h-3.5 w-3.5 text-indigo-500" />
            <span>Was soll der Agent tun?</span>
          </label>
          <select
            id="task-select"
            value={task}
            onChange={(e) => setTask(e.target.value as TaskType)}
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-xs font-medium text-neutral-900 shadow-2xs focus:border-purple-500 focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 min-h-[44px]"
          >
            <option value="battery_check">Batterien prüfen (&lt; 20% Restladung)</option>
            <option value="zombie_hunt">Stumme Zombie-Geräte finden (&gt; 24h offline)</option>
            <option value="heating_audit">Heiz-Detektiv (Auskühlrate &amp; Sparpotenzial)</option>
            <option value="log_audit">Logbuch nach Fehlern durchforsten (12h)</option>
            <option value="actuator_call">Thermostat schalten (Temperatur ändern)</option>
          </select>
          <span className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1">
            Bestimmt die Frage des Agenten an Home Assistant.
          </span>
        </div>

        {/* Strategy Selection */}
        <div className="flex flex-col justify-between rounded-xl border border-neutral-200/80 bg-neutral-50/50 p-4 dark:border-neutral-800/80 dark:bg-neutral-900/50">
          <label
            htmlFor="strategy-select"
            className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 flex items-center gap-1.5 mb-2"
          >
            <Zap className="h-3.5 w-3.5 text-amber-500" />
            <span>Wie schlau fragt der Agent?</span>
          </label>
          <select
            id="strategy-select"
            value={strategy}
            onChange={(e) => setStrategy(e.target.value as Strategy)}
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-xs font-medium text-neutral-900 shadow-2xs focus:border-purple-500 focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 min-h-[44px]"
          >
            <option value="filtered_mcp">Gezielter MCP-Filter (ha_search_entities) – Empfohlen</option>
            <option value="naive_dump">Naiv: Liest das ganze Haus auf einmal aus (Anti-Pattern)</option>
            <option value="lxc_worker">Ultra-kompakt: Vorfilter im Linux-Container</option>
          </select>
          <span className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1">
            Unterschied zwischen 400 Wörtern und 40.000 Wörtern!
          </span>
        </div>
      </div>

      {/* Human in the loop toggle if actuator */}
      {metrics.isActuator && (
        <div className="flex items-center justify-between rounded-xl border border-amber-500/30 bg-amber-50/50 p-3.5 dark:bg-amber-950/20">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <div>
              <span className="text-xs font-bold text-neutral-900 dark:text-neutral-100">
                Sicherheits-Bremse: Erst fragen, dann schalten
              </span>
              <p className="text-[11px] text-neutral-600 dark:text-neutral-400">
                Der Agent schaltet erst, wenn du auf dem Smartphone mit „Ja“ antwortest.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setHumanApproval(!humanApproval)}
            className={`rounded-lg px-4 py-2 text-xs font-semibold transition-colors min-h-[44px] ${
              humanApproval
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-neutral-200 text-neutral-800 hover:bg-neutral-300 dark:bg-neutral-800 dark:text-neutral-200"
            }`}
          >
            {humanApproval ? "Freigabe erteilt ✅" : "Blockiert (Frage nötig) ✋"}
          </button>
        </div>
      )}

      {/* Live KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-900/80">
          <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider">
            Wortmenge (Tokens)
          </span>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span
              className={`font-mono text-lg font-black ${
                metrics.tokenInput > 20000
                  ? "text-rose-600 dark:text-rose-400"
                  : metrics.tokenInput > 5000
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-emerald-600 dark:text-emerald-400"
              }`}
            >
              {metrics.tokenInput.toLocaleString("de-DE")}
            </span>
            <span className="text-[10px] text-neutral-400 font-mono">Tokens</span>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-900/80">
          <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider">
            Übertragene Daten
          </span>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="font-mono text-lg font-black text-neutral-900 dark:text-neutral-100">
              {metrics.transferBytes}
            </span>
            <span className="text-[10px] text-neutral-400 font-mono">KB</span>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-900/80">
          <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider">
            Reaktionszeit (LAN + KI)
          </span>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="font-mono text-lg font-black text-neutral-900 dark:text-neutral-100">
              {metrics.totalLatencyMs}
            </span>
            <span className="text-[10px] text-neutral-400 font-mono">ms</span>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-900/80">
          <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider">
            Sicherheits-Status
          </span>
          <div className="mt-1 flex items-center gap-1.5">
            {metrics.safetyStatus === "PASS" ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            ) : metrics.safetyStatus === "WARNUNG" ? (
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            ) : (
              <XCircle className="h-4 w-4 text-rose-500" />
            )}
            <span
              className={`font-mono text-sm font-bold ${
                metrics.safetyStatus === "PASS"
                  ? "text-emerald-600 dark:text-emerald-400"
                  : metrics.safetyStatus === "WARNUNG"
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-rose-600 dark:text-rose-400"
              }`}
            >
              {metrics.safetyStatus}
            </span>
          </div>
        </div>
      </div>

      {/* Safety Callout */}
      <div
        className={`rounded-lg border px-3.5 py-2 text-xs font-medium flex items-center gap-2 ${
          metrics.safetyStatus === "PASS"
            ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-800 dark:text-emerald-300"
            : metrics.safetyStatus === "WARNUNG"
            ? "border-amber-500/20 bg-amber-500/5 text-amber-800 dark:text-amber-300"
            : "border-rose-500/20 bg-rose-500/5 text-rose-800 dark:text-rose-300"
        }`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current shrink-0" />
        <span>{metrics.safetyMessage}</span>
      </div>

      {/* Output Tabs */}
      <div className="flex flex-col flex-1">
        <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setActiveTab("payload")}
              className={`border-b-2 px-3 py-2 text-xs font-semibold transition-colors min-h-[44px] ${
                activeTab === "payload"
                  ? "border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400"
                  : "border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Terminal className="h-3.5 w-3.5" />
                <span>1. Der Code-Befehl (MCP Payload)</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("markdown")}
              className={`border-b-2 px-3 py-2 text-xs font-semibold transition-colors min-h-[44px] ${
                activeTab === "markdown"
                  ? "border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400"
                  : "border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
              }`}
            >
              <span className="flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                <span>2. Der fertige Notizzettel (SilverBullet)</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("alternatives")}
              className={`border-b-2 px-3 py-2 text-xs font-semibold transition-colors min-h-[44px] ${
                activeTab === "alternatives"
                  ? "border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400"
                  : "border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Sliders className="h-3.5 w-3.5" />
                <span>3. Vergleich mit Hermes & Co.</span>
              </span>
            </button>
          </div>

          {activeTab !== "alternatives" && (
            <button
              type="button"
              onClick={() =>
                handleCopy(activeTab === "payload" ? jsonRpcPayload : markdownOutput)
              }
              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 min-h-[44px]"
              title="In Zwischenablage kopieren"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              <span>{copied ? "Kopiert!" : "Kopieren"}</span>
            </button>
          )}
        </div>

        {/* Tab Body */}
        <div className="mt-3 flex-1">
          {activeTab === "payload" && (
            <pre className="overflow-x-auto rounded-xl bg-neutral-900 p-4 font-mono text-xs text-neutral-200 dark:bg-black/80 max-h-[260px] leading-relaxed border border-neutral-800">
              <code>{jsonRpcPayload}</code>
            </pre>
          )}

          {activeTab === "markdown" && (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50/70 p-4 font-mono text-xs text-neutral-800 dark:border-neutral-800 dark:bg-neutral-900/60 dark:text-neutral-200 max-h-[260px] overflow-y-auto whitespace-pre-wrap leading-relaxed">
              {markdownOutput}
            </div>
          )}

          {activeTab === "alternatives" && (
            <div className="rounded-xl border border-neutral-200 p-4 dark:border-neutral-800 text-xs text-neutral-700 dark:text-neutral-300">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-purple-500/20 bg-purple-50/30 p-3 dark:border-purple-500/30 dark:bg-purple-950/20">
                  <span className="font-semibold text-purple-900 dark:text-purple-300">
                    Unser Setup: Antigravity + MCP
                  </span>
                  <p className="mt-1 text-[11px] text-neutral-600 dark:text-neutral-400">
                    <strong>Der Doku-Hausmeister:</strong> Braucht nur ~350 MB RAM, erzeugt echte Notizdateien in SilverBullet und läuft stabil im lokalen Proxmox-Container.
                  </p>
                </div>
                <div className="rounded-lg border border-neutral-100 bg-neutral-50/50 p-3 dark:border-neutral-800 dark:bg-neutral-900/50">
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    Hermes Agent (Nous Research)
                  </span>
                  <p className="mt-1 text-[11px] text-neutral-500">
                    <strong>Der Messenger-Begleiter:</strong> Verbindet Telegram, Discord und Signal direkt mit Home Assistant. Ideal für Chat-Befehle unterwegs, legt aber kein Notizbuch an.
                  </p>
                </div>
                <div className="rounded-lg border border-neutral-100 bg-neutral-50/50 p-3 dark:border-neutral-800 dark:bg-neutral-900/50">
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    Wyoming / Rhasspy (Hermes MQTT)
                  </span>
                  <p className="mt-1 text-[11px] text-neutral-500">
                    <strong>Der reine Sprach-Lautsprecher:</strong> Reagiert auf Wake-Words wie „Hey Nabu“ an Konferenztisch-Mikrofonen, führt aber keine System-Audits durch.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
